// Wave 20.47g — position detail page.
//
// Route: /positions/:class. Big pose art hero, editable name, all
// sessions where this position was recorded, and a sparkline of
// per-session bests. Rider can rename the position from here in
// addition to the aero pane; both routes hit the same
// PATCH /users/me/position-aliases/:class endpoint.

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import PoseGlyph from './components/PoseGlyph';
import { colors } from './theme';
import { positionColor } from './services/positionColors';
import { usePositionsAnalytics } from './hooks/usePositionsAnalytics';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

async function saveAlias(positionClass: string, name: string) {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) throw new Error('no token');
  const res = await fetch(`${API}/users/me/position-aliases/${encodeURIComponent(positionClass)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`PATCH failed (${res.status})`);
}

// Wave 20.49m — rider-triggered deletion of a single measurement
// (one session's contribution) or the entire position (every
// session's contribution).
async function deletePositionMeasurement(positionClass: string, sessionId: string) {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) throw new Error('no token');
  const res = await fetch(
    `${API}/sessions/me/positions/${encodeURIComponent(positionClass)}/measurements/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } },
  );
  if (!res.ok) throw new Error(`DELETE failed (${res.status})`);
}

async function deletePositionAll(positionClass: string) {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) throw new Error('no token');
  const res = await fetch(
    `${API}/sessions/me/positions/${encodeURIComponent(positionClass)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } },
  );
  if (!res.ok) throw new Error(`DELETE failed (${res.status})`);
}

function formatHoldMinutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

function relativeWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const delta = Math.max(0, (now - d.getTime()) / 1000);
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
  const days = Math.round(delta / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function PositionDetail() {
  const { class: positionClass = '' } = useParams<{ class: string }>();
  const nav = useNavigate();
  const { data, loading, error, errorMessage, reload } = usePositionsAnalytics();

  const p = useMemo(
    () => data?.positions.find((x) => x.class === positionClass) ?? null,
    [data, positionClass],
  );

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyDelete, setBusyDelete] = useState<string | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);

  const onDeleteMeasurement = async (sessionId: string) => {
    if (!p) return;
    if (
      !window.confirm(
        `Remove this session's ${p.alias || p.class} measurement from your analytics? The session itself will remain.`,
      )
    ) return;
    setBusyDelete(sessionId);
    try {
      await deletePositionMeasurement(p.class, sessionId);
      reload();
    } catch {
      /* silent — a failing retry is safe */
    } finally {
      setBusyDelete(null);
    }
  };

  const onDeletePosition = async () => {
    if (!p) return;
    if (
      !window.confirm(
        `Delete every ${p.alias || p.class} measurement across all your sessions? This clears the position from the aero pane and the wake. Your sessions stay intact.`,
      )
    ) return;
    setDeletingAll(true);
    try {
      await deletePositionAll(p.class);
      nav('/session');
    } catch {
      setDeletingAll(false);
    }
  };

  const openEdit = () => {
    setDraft(p?.alias || p?.class || '');
    setEditing(true);
  };
  const submitEdit = async () => {
    const name = draft.trim().slice(0, 64);
    if (!p || !name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await saveAlias(p.class, name);
      setEditing(false);
      reload();
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout maxWidth="md">
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Stack>
      </PageLayout>
    );
  }
  if (error || !p) {
    return (
      <PageLayout maxWidth="md">
        <Stack spacing={2} sx={{ py: 4 }}>
          <Alert severity="warning">{errorMessage || 'Position not found.'}</Alert>
          <Button component={RouterLink} to="/feed" startIcon={<ArrowBackIcon />} sx={{ color: colors.brand }}>
            Back to Feed
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  const c = positionColor(p.class);
  const displayName = p.alias || p.class;
  const recent = (p.recentBests ?? []).slice().sort((a, b) => (a.startedAt < b.startedAt ? -1 : 1));

  return (
    <PageLayout maxWidth="md">
      <Stack spacing={2.5} sx={{ py: 3 }}>
        <Button
          onClick={() => nav(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'flex-start', color: colors.textMuted, textTransform: 'none' }}
          size="small"
        >
          Back
        </Button>

        {/* Hero: pose art + editable name + primary stat */}
        <Box
          sx={{
            borderRadius: 3,
            border: `1px solid ${c}55`,
            backgroundColor: 'rgba(15,15,20,0.75)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(10,14,26,0.6)',
              py: 3,
            }}
          >
            <PoseGlyph positionClass={p.class} size={340} />
          </Box>
          <Box sx={{ px: 3, py: 2.5, borderTop: `1px solid ${colors.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              {editing ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 64))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void submitEdit();
                      if (e.key === 'Escape') setEditing(false);
                    }}
                    sx={{ maxWidth: 320, '& input': { fontSize: '1.1rem', fontWeight: 700 } }}
                  />
                  <IconButton onClick={submitEdit} disabled={saving} sx={{ color: colors.brand }}>
                    <CheckIcon />
                  </IconButton>
                  <IconButton onClick={() => setEditing(false)} disabled={saving} sx={{ color: colors.textMuted }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ) : (
                <>
                  <Typography sx={{ color: c, fontWeight: 900, fontSize: '1.4rem', letterSpacing: '0.02em' }}>
                    {displayName.toUpperCase()}
                  </Typography>
                  <IconButton size="small" onClick={openEdit} sx={{ color: colors.textFaint }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {!editing && p.alias && (
                <Typography sx={{ color: colors.textFaint, fontSize: '0.75rem', fontStyle: 'italic' }}>
                  (from {p.class})
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={3}>
              <Stat label="Best CdA" value={p.bestCda.toFixed(3)} accent={colors.brand} />
              <Stat label="Avg" value={p.meanCda.toFixed(3)} />
              <Stat label="Worst" value={p.worstCda.toFixed(3)} />
              <Stat label="Sessions" value={String(p.sessionCount)} />
              <Stat label="Held" value={formatHoldMinutes(p.totalHoldSec)} />
            </Stack>
          </Box>
        </Box>

        {/* Wave 20.49r — Calibration consistency panel. One frame,
            all sessions' shoulder + hip dots stacked. The tighter
            the two clusters, the more repeatable the rider's
            framing across sessions. Rows without a snapshot (older
            sessions) are skipped. Hidden when nothing to show. */}
        <CalibrationConsistencyPanel entries={recent} accent={c} />

        {/* Session history */}
        <Box
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            p: 2.5,
          }}
        >
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Session History
          </Typography>
          {recent.length === 0 ? (
            <Typography sx={{ color: colors.textMuted, mt: 1 }}>
              No sessions recorded yet — record more to see your CdA trend.
            </Typography>
          ) : (
            <>
              {/* Wave 20.47h — sparkline of per-session bests, parity
                  with mobile PositionDetailScreen. Y is inverted so
                  up-and-to-the-right = getting more aero. */}
              {recent.length >= 2 && (
                <Box sx={{ mt: 1.25 }}>
                  <HistorySparkline values={recent.map((r) => r.bestCda)} color={c} />
                </Box>
              )}
            </>
          )}
          {recent.length > 0 && (
            <Stack spacing={1} sx={{ mt: 1.25 }}>
              {recent.slice().reverse().map((r, i) => {
                const isBest = r.sessionId === p.bestSessionId;
                const busy = busyDelete === r.sessionId;
                return (
                  <Box
                    key={r.sessionId + i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: 2,
                      border: `1px solid ${isBest ? c : colors.border}`,
                      backgroundColor: isBest ? `${c}12` : colors.surfaceElevated,
                      transition: 'border-color 120ms',
                      '&:hover': { borderColor: c },
                    }}
                  >
                    <Box
                      component={RouterLink}
                      to={`/sessions/${r.sessionId}`}
                      sx={{
                        flex: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.9rem' }}>
                          {new Date(r.startedAt).toLocaleDateString(undefined, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Typography>
                        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                          {relativeWhen(r.startedAt)}
                          {isBest && (
                            <span style={{ color: c, marginLeft: 8, fontWeight: 800 }}>· BEST</span>
                          )}
                          {/* Wave 20.49s — CdA on this row was NOT
                              rig-normalized. Either the session
                              pre-dates the calibration snapshot,
                              its pose was too far from the rider's
                              historical centroid, or the rider
                              doesn't have enough calibration
                              samples yet. Rider deserves to know
                              this number is raw, not corrected. */}
                          {r.cdaNormalized === false && (
                            <span
                              style={{
                                color: colors.textFaint,
                                marginLeft: 8,
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                letterSpacing: '0.08em',
                              }}
                              title="Not rig-normalized — this row shows the raw camera-observed CdA."
                            >
                              · RAW
                            </span>
                          )}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 900,
                          fontSize: '1.1rem',
                          color: isBest ? c : colors.text,
                        }}
                      >
                        {r.bestCda.toFixed(3)}
                      </Typography>
                    </Box>
                    {/* Wave 20.49m — per-measurement delete. Removes
                        this class's entry from this one session's
                        positions[]; the session itself stays. */}
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={(e) => {
                        e.preventDefault();
                        void onDeleteMeasurement(r.sessionId);
                      }}
                      aria-label="Delete this measurement"
                      sx={{
                        color: colors.textMuted,
                        '&:hover': { color: colors.danger },
                      }}
                    >
                      {busy ? (
                        <CircularProgress size={16} sx={{ color: colors.textMuted }} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Wave 20.49m — danger zone. Deletes every recording of
            this position across all the rider's sessions. The
            sessions themselves stay intact; the position just
            disappears from the aero pane + wake analytics. */}
        <Box
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            p: 2.5,
          }}
        >
          <Typography variant="overline" sx={{ color: colors.danger, letterSpacing: '0.2em' }}>
            Danger zone
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.5, mb: 1.5 }}>
            Remove every measurement of {p.alias || p.class} from your analytics. Your recorded
            sessions stay intact — only this position's contribution disappears.
          </Typography>
          <Button
            variant="outlined"
            onClick={onDeletePosition}
            disabled={deletingAll}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              color: colors.danger,
              borderColor: colors.danger,
              '&:hover': { borderColor: colors.danger, backgroundColor: 'rgba(255,71,87,0.06)' },
            }}
          >
            {deletingAll ? 'Deleting…' : 'Delete this position'}
          </Button>
        </Box>
      </Stack>
    </PageLayout>
  );
}

// Wave 20.49r — Calibration consistency panel. One frame at the
// top of PositionDetail with every session's shoulder + hip dot
// overlaid so the rider can see, at a glance, how repeatable
// their framing has been across sessions. The tighter the two
// clusters, the more consistent — which directly answers the
// user's "how aligned are these" question.
//
// Alongside the dots we compute a small numeric spread
// (max distance between any two entries, expressed as % of frame
// width) so the rider has a hard number they can watch trend
// down over time as they dial in their setup.

type SnapshotEntry = {
  sessionId: string;
  startedAt: string;
  calibrationSnapshot?: {
    shoulderMid: { x: number; y: number };
    hipMid: { x: number; y: number };
    silhouettePngB64: string | null;
    capturedAt: string;
  } | null;
};

function CalibrationConsistencyPanel({
  entries,
  accent,
}: {
  entries: SnapshotEntry[];
  accent: string;
}) {
  const withSnap = entries.filter(
    (r): r is SnapshotEntry & {
      calibrationSnapshot: {
        shoulderMid: { x: number; y: number };
        hipMid: { x: number; y: number };
        silhouettePngB64: string | null;
        capturedAt: string;
      };
    } => !!r.calibrationSnapshot,
  );
  if (withSnap.length === 0) return null;

  // Newest snapshot's silhouette is the background so the rider
  // can see the dots in context of a real body outline. Older
  // silhouettes could be layered too, but they'd wash the frame
  // out — one clean background reads better.
  const newestByDate = withSnap.slice().sort((a, b) =>
    a.startedAt < b.startedAt ? 1 : -1,
  );
  const backgroundSilhouette = newestByDate[0].calibrationSnapshot.silhouettePngB64;

  // Spread = greatest pairwise distance in normalized-frame
  // coordinates, computed separately for shoulder and hip dots.
  // Expressed as % of frame width so the number is unit-friendly.
  const spread = (
    pts: Array<{ x: number; y: number }>,
  ): number => {
    if (pts.length < 2) return 0;
    let max = 0;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x;
        const dy = pts[i].y - pts[j].y;
        const d = Math.hypot(dx, dy);
        if (d > max) max = d;
      }
    }
    return max;
  };
  const shoulderPts = withSnap.map((r) => r.calibrationSnapshot.shoulderMid);
  const hipPts = withSnap.map((r) => r.calibrationSnapshot.hipMid);
  const shoulderSpreadPct = Math.round(spread(shoulderPts) * 100);
  const hipSpreadPct = Math.round(spread(hipPts) * 100);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
        p: 2.5,
      }}
    >
      <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
        Calibration Alignment
      </Typography>
      <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.5, mb: 2 }}>
        Every session's locked-in shoulder + hip dots stacked on one
        frame. Tighter clusters = more repeatable framing.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
        <Box
          sx={{
            position: 'relative',
            width: 220,
            height: 220,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surfaceElevated,
            overflow: 'hidden',
            flex: '0 0 auto',
          }}
        >
          {backgroundSilhouette && (
            <Box
              component="img"
              src={backgroundSilhouette}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0.35,
              }}
            />
          )}
          {withSnap.map((r) => {
            const shoulder = r.calibrationSnapshot.shoulderMid;
            const hip = r.calibrationSnapshot.hipMid;
            return (
              <React.Fragment key={r.sessionId}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${shoulder.x * 100}%`,
                    top: `${shoulder.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: accent,
                    border: '1.5px solid #0e0e12',
                    opacity: 0.85,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${hip.x * 100}%`,
                    top: `${hip.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: accent,
                    border: '1.5px solid #0e0e12',
                    opacity: 0.65,
                  }}
                />
              </React.Fragment>
            );
          })}
        </Box>
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          <Stat label="Alignments" value={String(withSnap.length)} />
          <Stat
            label="Shoulder spread"
            value={`${shoulderSpreadPct}%`}
            accent={shoulderSpreadPct <= 5 ? accent : undefined}
          />
          <Stat
            label="Hip spread"
            value={`${hipSpreadPct}%`}
            accent={hipSpreadPct <= 5 ? accent : undefined}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function HistorySparkline({ values, color }: { values: number[]; color: string }) {
  const width = 560;
  const height = 44;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1e-6, max - min);
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * (width - 8) + 4;
      // Inverted Y — lower CdA = higher on chart.
      const y = height - 4 - ((max - v) / range) * (height - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
      role="img"
      aria-label="Recent per-session best CdAs"
    >
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.9}
      />
    </svg>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <Box>
      <Typography sx={{ color: colors.brand, fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.15em' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 800,
          fontSize: '1.05rem',
          color: accent || colors.text,
          mt: 0.25,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
